/*
 * Authors: mariushirjanu, Koen
 * Created: 20/04/2015
 * Upgraded: 27/12/2022
 */
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { BILLINGTYPE_CENTRAL, BILLINGTYPE_LOCAL, PAYMENTTYPE_PO, PAYMENTTYPE_PP } from '../../../../shared/services/billing/billing-data.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import { GaWizardService } from '../../../../shared/wizard/ga-wizard.service';
import { OrderEntryPaymentService } from './order-entry-payment.service';
import { WIZARD_ORDER } from '../../../../shared/app-wizards';
import { GaWizard } from '../../../../shared/wizard/ga-wizard';
import { IDescribedStringObject } from '../../../../../type_definitions/datatypes';
import { POType } from '../../../payment/payment-data.service';
import { GaValidateService, IValidatingComponent } from '../../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { OrderEntryInfoService } from '../order-info/order-entry-info.service';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { OrderEntryStepId } from '../order-entry-steps';

interface IOrderEntryPaymentModel {
  paymentType:IDescribedStringObject;
  customerPONumber:string;
}

interface IOrderEntryPaymentTermsModel {
  localPaymentTerms:number;
  centralPaymentTerms:number;
}

@Component({
  selector: 'order-entry-payment',
  template: `
    <h2 translate>ORDERENTRY.PAYMENT.TERMS</h2>
    
    <div class="alert alert-danger" *ngIf="errorMessage">
      <h4 class="alert-heading"><i class="fa fa-exclamation-circle"></i>&nbsp;<span translate>ORDERENTRY.PAYMENT.PAYMENT_FAILED_TITLE</span></h4>
      <span [innerHTML]="'ORDERENTRY.PAYMENT.PAYMENT_FAILED_MESSAGE' | translate: {errorMessage: errorMessage}"></span>
    </div>
    
    <div class="order-payment">
      <formly [form]="paymentTermsForm" [fields]="paymentTermsFields" [model]="paymentTermsModel" class="form-horizontal"></formly>
    </div>
    <h2 translate>ORDERENTRY.PAYMENT.TYPE</h2>
    <div class="order-payment">
      <formly [form]="paymentTypeForm" [fields]="paymentTypeFields" [model]="paymentModel" class="form-horizontal"></formly>
      <div *ngIf="poFields?.length">
        <h2 translate>ORDERENTRY.PAYMENT.PO_NUMBER</h2>
        <formly [form]="paymentForm" [fields]="poFields" [model]="paymentModel" class="form-horizontal"></formly>
      </div>
    
      <div *ngIf="paymentModel.paymentType && paymentModel.paymentType.id !== '${PAYMENTTYPE_PO}'">
        <img src="assets/img/paypal-logo.png" alt="Paypal logo" class="paypal-logo" *ngIf="paymentModel.paymentType.id === '${PAYMENTTYPE_PP}'"/>
        <br/>
        <div><i class="fa fa-info-circle"></i>&nbsp;<span translate>ORDERENTRY.PAYMENT.PAYMENT_LINK_INFO</span></div>
      </div>
    </div>
  `,
  styleUrls: ['./order-entry-payment.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderEntryPaymentComponent implements IValidatingComponent, OnInit, AfterViewInit, OnDestroy {
  @Input() public errorMessage:string;

  public poFields:IExtraFieldConfigurationObject[];
  public paymentTypeFields:IExtraFieldConfigurationObject[];
  public paymentTermsFields:IExtraFieldConfigurationObject[];
  public paymentTermsForm:UntypedFormGroup = new UntypedFormGroup({});
  public paymentTypeForm:UntypedFormGroup = new UntypedFormGroup({});
  public paymentForm:UntypedFormGroup = new UntypedFormGroup({});
  public paymentModel:IOrderEntryPaymentModel;
  public paymentTermsModel:IOrderEntryPaymentTermsModel;
  public poType:string;

  private wizard:GaWizard;
  private possiblePaymentTypes:IDescribedStringObject[];
  private onValidationReadyDeferred:IGADeferred<void>; // resolved when the component is ready for validation
  private poNumberField:IExtraFieldConfigurationObject;

  constructor(
    private translationService:TranslationService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private orderEntryInfoService:OrderEntryInfoService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private validateService:GaValidateService,
    private wizardService:GaWizardService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.initWizardSetup();

    this.paymentModel = {
      paymentType: this.orderEntryPaymentService.paymentType,
      customerPONumber: this.orderEntryInfoService.customerPONumber
    };

    this.paymentTermsModel = {
      localPaymentTerms: this.orderEntryPaymentService.localPaymentTerms,
      centralPaymentTerms: this.orderEntryPaymentService.centralPaymentTerms
    };

    this.possiblePaymentTypes = this.orderEntryPaymentService.paymentTypes;
    this.poType = this.orderEntryWebshopService.shopContext?.poType;

    if (!this.paymentModel.paymentType) {
      this.paymentModel.paymentType = this.possiblePaymentTypes[0];
      this.orderEntryPaymentService.paymentType = this.possiblePaymentTypes[0];
    }

    this.createFields();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private initWizardSetup():void {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);

    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, OrderEntryStepId.PAYMENT);
  }

  private createFields():void {
    if ([POType.MANUAL.toString(), POType.MANDATORY.toString()].indexOf(this.poType) > -1) {
      this.poFields = [
        this.poNumberField = this.formlyService.anInput('customerPONumber', 'ORDERENTRY.PAYMENT.PONR')
          .withRequired(this.poNumberRequired())
          .withMaxLength(40)
          .withLabelClass('col-xsm-2 col-sm-2 col-md-2')
          .withValueClass('col-xsm-6 col-sm-4 col-md-3')
          .withPlaceholder('ORDERENTRY.PAYMENT.PONR_PH')
          .onChange((customerPONumber:string) => {
            this.orderEntryInfoService.customerPONumber = customerPONumber;
          })
          .build()
      ];
    }

    this.paymentTypeFields = [
      this.formlyService.aRadio('paymentType', undefined)
        .isRequired()
        .withFormat(this.formattingService.formatLov)
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withOptions(this.possiblePaymentTypes)
        .withExtraOptions({
          templateOptions: {
            inline: true
          }
        })
        .onChange((paymentType:IDescribedStringObject) => {
          this.orderEntryPaymentService.paymentType = paymentType;
        })
        .build()
    ];

    this.paymentTermsFields = [
      this.formlyService.aValue('localPaymentTerms', 'ORDERENTRY.PAYMENT.TERMS')
        .withLabelClass('col-xsm-2 col-sm-2 col-md-2')
        .withValueClass('col-xsm-6 col-sm-4 col-md-3')
        .withFormat(this.formattingService.formatPaymentTerms)
        .withHideExpression(() => this.orderEntryPaymentService.billingType === BILLINGTYPE_CENTRAL) //locationTo
        .build(),

      this.formlyService.aValue('centralPaymentTerms', 'ORDERENTRY.PAYMENT.TERMS')
        .withLabelClass('col-xsm-2 col-sm-2 col-md-2')
        .withValueClass('col-xsm-6 col-sm-4 col-md-3')
        .withFormat(this.formattingService.formatPaymentTerms)
        .withHideExpression(() => this.orderEntryPaymentService.billingType === BILLINGTYPE_LOCAL)
        .build()
    ];
  }

  public beforeValidate = ():Promise<void> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    if ((this.poType === POType.MANDATORY || this.paymentModel.paymentType?.id === PAYMENTTYPE_PO) && this.poFields) {
      return this.validateService.validateFields(
        this.poFields,
        this.paymentModel,
        true
      );
    }

    return Promise.resolve();
  };

  private poNumberRequired():boolean {
    return ((this.orderEntryWebshopService.shopContext.orderPermissions && this.orderEntryWebshopService.shopContext.orderPermissions.allowSubmit) || this.orderEntryWebshopService.shopContext.orderApprovalPOMandatory)
      && [POType.MANUAL.toString(), POType.MANDATORY.toString()].indexOf(this.poType) > -1;
  }

  public ngOnDestroy():void {
    this.wizard.clearCurrentValidator();
  }
}